'use client';
import React, { useEffect, useState } from 'react';

import {
  isDocumentImageKey,
  resolveDocumentImageUrl,
} from '@/services/util/document-image-resolver';
import { normalizeDocumentImageUrl } from '@/services/util/document-image-url';

interface Props {
  src: string;
  alt?: string;
  title?: string;
  className?: string;
  widthAttr?: string;
  alignAttr?: string;
  draggable?: boolean;
}

type ResolveStatus = 'loading' | 'loaded' | 'error';

export default function DocumentImageResolved({
  src,
  alt = '',
  title,
  className,
  widthAttr,
  alignAttr,
  draggable,
}: Props) {
  const isKey = isDocumentImageKey(src);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(
    isKey ? null : normalizeDocumentImageUrl(src),
  );
  const [status, setStatus] = useState<ResolveStatus>(isKey ? 'loading' : 'loaded');
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!isDocumentImageKey(src)) {
      setResolvedUrl(normalizeDocumentImageUrl(src));
      setStatus('loaded');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    resolveDocumentImageUrl(src, { force: retryToken > 0 })
      .then((url) => {
        if (cancelled) return;
        setResolvedUrl(url);
        setStatus('loaded');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [src, retryToken]);

  const handleRetry = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setRetryToken((token) => token + 1);
  };

  if (status === 'error') {
    return (
      <div
        className={`assistiv-image-placeholder assistiv-image-placeholder--error ${className ?? ''}`}
        data-width={widthAttr}
        data-align={alignAttr}
      >
        <span>Image failed to load</span>
        <button type='button' onClick={handleRetry}>
          Retry
        </button>
      </div>
    );
  }

  if (status === 'loading' || !resolvedUrl) {
    return (
      <div
        className={`assistiv-image-placeholder assistiv-image-placeholder--loading ${className ?? ''}`}
        data-width={widthAttr}
        data-align={alignAttr}
        role='status'
        aria-label='Loading image'
      />
    );
  }

  return (
    <img
      src={resolvedUrl}
      alt={alt}
      title={title}
      className={className}
      data-width={widthAttr}
      data-align={alignAttr}
      draggable={draggable}
      onError={() => setStatus('error')}
    />
  );
}
